class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    // Enrichment: --header-height must be available before the FIRST user
    // interaction, not just after it -- delayUntilInteraction alone meant
    // that if opening the search/mega-menu overlay WAS that first
    // interaction, the click could fire before setHeight() had run, leaving
    // --header-height unset and the overlay falling back to top:0 (wrong
    // position/z-order, intermittent depending on click timing). Calling
    // setHeight() immediately here fixes that; delayUntilInteraction stays
    // as a harmless redundant safety net for later layout shifts.
    this.setHeight();
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      // Cache layout computations to reduce reflow
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);

      // Enrichment: cached on the instance (not just written to the CSS
      // var) so StickyHeader.onScrollHandler can restore this exact value
      // later -- see the comment on that method for why --header-offset-top
      // can't just stay at this natural-flow value once the header is
      // pinned mid-scroll.
      // Enrichment (2026-10-16): only trust offsetTop while the header is in
      // normal flow. Once it is pinned, parentElement.offsetTop reports the
      // SCROLL OFFSET, not the announcement bar's height -- measured live at
      // scrollY 700 it returned exactly 700. Caching that poisons the value
      // that syncOffsetTopForPinnedState() restores on unpin.
      const isPinnedNow = document.body.classList.contains('header-pinned');
      if (!isPinnedNow) {
        this._headerOffsetTopNatural = offsetTop;
      }

      // Enrichment (2026-09-06): distance from the TOP OF THE HEADER BOX
      // down to the nav row specifically (.header__navigation), for the
      // search dropdown -- it wants to start where the nav row begins, not
      // where the whole header ends, so it visually covers the nav (and its
      // mega-menu triggers) while leaving the logo/account/cart row above it
      // fully visible and clickable. Measured via getBoundingClientRect
      // subtraction rather than offsetTop-chain math: both rects are read
      // in the same instant, so scroll position cancels out exactly
      // regardless of whether anything between .header and .header__navigation
      // establishes its own offsetParent. This value is a fixed layout
      // constant (the nav row's position relative to the header's OWN top
      // edge never changes with scroll or sticky-pinning, only the header's
      // own page position does) -- that page-position part is already
      // handled correctly by --header-offset-top, so this only needs
      // recomputing on resize/load, same as --header-height, not on every
      // scroll frame. Falls back to the full header height when no
      // .header__navigation exists (e.g. a header layout without a visible
      // desktop nav row), so the search dropdown degrades to covering the
      // whole header rather than breaking.
      const navElement = this.querySelector('.header__navigation');
      const navOffsetInHeader = navElement
        ? Math.round(navElement.getBoundingClientRect().top - this.getBoundingClientRect().top)
        : offsetHeight;

      // Batch style updates
      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      // Enrichment (2026-10-16): this write used to be unconditional, which is
      // why the search dropdown opened in the wrong place. setHeight() runs on
      // a 5s timer (delayUntilInteraction's fallback), on the first mouse move,
      // and on every ResizeObserver callback -- any of those firing while the
      // header is pinned clobbered the 0 that syncOffsetTopForPinnedState() had
      // set, with the scroll offset. The panel's top is
      // calc(--header-offset-top + --header-nav-offset), so it rendered that far
      // down the page and only "snapped" back on the next scroll frame.
      // Measured live before this fix: pinned at scrollY 700, --header-offset-top
      // 700px, panel top 790px instead of 90px.
      document.documentElement.style.setProperty(
        '--header-offset-top',
        `${isPinnedNow ? 0 : offsetTop}px`
      );
      document.documentElement.style.setProperty('--header-nav-offset', `${navOffsetInHeader}px`);
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.classes = {
      pinned: 'header-pinned',
    };
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;

    // Cache initial bounds for better performance
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.onScrollHandler.bind(this), false);
  }

  // Enrichment (2026-09-06): --header-offset-top is the header's distance
  // from the page top IN NORMAL DOCUMENT FLOW (i.e. the announcement bar's
  // height, when one is showing) -- it's only ever recomputed by
  // setHeight() on resize/load, never on scroll. That's correct while the
  // header sits in its normal flow position, but once the user scrolls past
  // it and the sticky behavior pins it flush at the true viewport top
  // (body.header-pinned, see below), the header's REAL on-screen offset is
  // 0, not the announcement bar's height -- the search dropdown (which
  // reads this variable to sit directly under the header, see
  // CODE_HEAD_MIRROR.md) was rendering with a stale gap equal to the
  // announcement bar's height in that state. Keeping this in sync with the
  // pinned state, right alongside the existing class toggles below, so it's
  // never stale by more than one scroll-driven rAF frame.
  syncOffsetTopForPinnedState() {
    const isPinned = document.body.classList.contains(this.classes.pinned);
    const value = isPinned ? 0 : this._headerOffsetTopNatural ?? 0;
    document.documentElement.style.setProperty('--header-offset-top', `${value}px`);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
    const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add('header-scrolled');

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.classes.pinned);
        } else {
          // Sticky on scroll up.
          if (scrollTop < this.currentScrollTop || scrollTop < headerBoundsBottom + 100) {
            document.body.classList.add(this.classes.pinned);
          } else {
            document.body.classList.remove(this.classes.pinned);
          }
        }
      } else {
        headerSection.classList.remove('header-scrolled');
        document.body.classList.remove(this.classes.pinned);
      }

      this.syncOffsetTopForPinnedState();
      this.currentScrollTop = scrollTop;
    });
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

/* ==========================================================================
   2026-09-05 (mega-menu rewrite, point 2)

   Show/hide is no longer animated by FoxTheme.Motion.animate(). That library
   was being asked to animate the `visibility` property itself, which it fails
   to do silently in Chrome -- the menu stayed invisible while genuinely open,
   so the site's primary navigation was unreachable with nothing logged
   anywhere. Visibility is now pure CSS keyed off the native <details> [open]
   attribute (see theme.css, the ".dropdown, .mega-menu" block).

   What JS still owns: toggling `open`, hover-intent, outside-click,
   Escape-to-close, focus handling, and the edge-of-viewport flip check.
   What it no longer owns: whether the nav is visible at all.

   2026-09-23 (Safari/WebKit hover fix)

   DetailsDropdown/DetailsMega used to be "customized built-in elements":
   customElements.define('details-dropdown', DetailsDropdown, {extends:
   'details'}), each class extending HTMLDetailsElement. WebKit has never
   implemented that part of the Custom Elements spec (a long-standing,
   publicly stated Apple/WebKit position) -- so on Safari and every iOS
   browser (all iOS browsers are WebKit), is="details-dropdown"/
   is="details-mega" elements never upgrade, the constructor never runs, and
   every listener wired up below (hover-intent, click, Escape, focus) simply
   doesn't exist. Only the plain native <details>/<summary> click-to-toggle
   survives, which is why hovering the nav did nothing on Safari/iOS while a
   bare click on the summary looked like it "sort of" worked.

   Fix: stop depending on the customized-built-in upgrade mechanism.
   DropdownController below is a plain class -- not an HTMLElement subclass
   -- that takes an already-parsed <details> element and wires up exactly
   the same listeners the old constructor did. initDropdownControllers()
   instantiates one per matching element instead of relying on the browser
   to upgrade them. This works identically in every browser because it
   doesn't depend on any custom-element upgrade at all.

   DetailsMega no longer needs to exist as its own class. It used to be an
   empty subclass purely because customElements.define() refuses to register
   one constructor under two tag names; that restriction doesn't apply to a
   plain class instantiated imperatively, so is="details-dropdown" and
   is="details-mega" elements both just get a DropdownController now. Its
   only real differences from a plain dropdown were its transform curve (a
   CSS custom property, --mm-slide-*, untouched here) and the theme-editor
   block select/deselect handlers, which were already shared logic.

   The is="details-dropdown"/is="details-mega" markup attributes are left in
   place in the .liquid files -- inert without the old customElements.define
   registration, but harmless, and removing them would be a pure markup diff
   with no behavioural benefit.
   ========================================================================== */

// Hover-intent timings. The previous code set `this.hoverTimer = null` in the
// constructor and never read it again -- confirmed dead, so hover was fully
// undebounced and menus flew open whenever the pointer merely crossed a
// trigger on its way elsewhere. Closing is more forgiving than opening so a
// brief slip off the panel does not dismiss it.
const NAV_HOVER_OPEN_DELAY = 80;
const NAV_HOVER_CLOSE_DELAY = 200;

/* Small standalone helpers rather than a shared base class. MenuDrawerDetails
   further down is a genuinely different interaction pattern (a drill-down
   panel stack) built on DrawerComponent, which CartDrawer, SearchDrawer and
   QuickViewModal also extend -- giving mobile nav a common ancestor with the
   desktop dropdown would put cart and search inside the blast radius of a
   nav-only change. Composition instead: whatever needs these behaviours
   calls them. */
function isEscapeKey(event) {
  return event.code === 'Escape' || event.key === 'Escape';
}

function isOutsideClick(element, event) {
  return !element.contains(event.target);
}

function createHoverIntent(onSettled) {
  let timer = null;
  return {
    schedule(shouldOpen) {
      clearTimeout(timer);
      timer = setTimeout(
        () => onSettled(shouldOpen),
        shouldOpen ? NAV_HOVER_OPEN_DELAY : NAV_HOVER_CLOSE_DELAY
      );
    },
    cancel() {
      clearTimeout(timer);
      timer = null;
    },
  };
}

/* 2026-09-05: mega-menu promo images render blank for the duration of their
   own fetch the first time a menu is opened.

   Root cause: card-image.liquid is a sitewide snippet and ships its <img> with
   loading="lazy". A closed nav panel is laid out but never painted, so the
   browser has no reason to fetch the image until the panel is actually opened
   -- at which point the user is already looking at an empty card. It affects
   whichever menu is opened first with an uncached image, which is why it reads
   as "one specific menu is broken" depending on hover order.

   This is PRE-EXISTING, not introduced by this rewrite: measured on the live
   published theme, opening a menu cold gives complete=false/naturalWidth=0 at
   the instant of open and complete=true/naturalWidth=2200 a few seconds later,
   identical to here. It is fixed rather than left alone because an empty promo
   card reads as a broken menu, and because doing it in the nav's own JS avoids
   touching the shared snippet that every other card on the site uses.

   2026-09-23: moved from "first hover anywhere in the nav" to page-idle.
   Own report: testing hover across all 5 top-level items in a normal
   left-to-right sweep, the first couple opened cleanly but the later ones
   looked "glitchy" -- consistent with this exact race, just more visible
   under a fast sweep than a single normal hover. Since warmNavImages()
   only flips `loading` to eager and doesn't wait for the fetch, ALL 5
   menus' images start downloading at once regardless of hover order (they
   always did -- this function has never been per-item), but they still
   take a moment to actually finish; the later items in a fast sweep are
   simply more likely to still be mid-fetch when looked at. Warming on
   page-idle instead of first-hover gives every menu's images the maximum
   possible head start before any interaction at all, so by the time a
   real visitor's cursor reaches the nav (let alone sweeps across 5 items
   in under a second), the fetches are already well underway or finished.
   requestIdleCallback with a setTimeout fallback (Safari has never
   implemented requestIdleCallback) -- deliberately NOT synchronous at
   load, so this still doesn't compete with above-the-fold rendering. */
let navImagesWarmed = false;
function warmNavImages() {
  if (navImagesWarmed) return;
  navImagesWarmed = true;
  document
    .querySelectorAll('.mega-menu img[loading="lazy"], .dropdown img[loading="lazy"]')
    .forEach((img) => {
      img.loading = 'eager';
    });
}
if (window.requestIdleCallback) {
  requestIdleCallback(warmNavImages, { timeout: 2000 });
} else {
  setTimeout(warmNavImages, 1500);
}

// Maps a controlled <details> element to its controller instance. Doubles
// as the "is this details element one of ours" check that used to be an
// `instanceof DetailsDropdown` test.
const dropdownControllers = new WeakMap();

// Shared open-dropdown counter driving body.has-dropdown-menu. Previously a
// WeakMap keyed on the DetailsDropdown *class itself* -- a roundabout way to
// get one counter shared across both DetailsDropdown and DetailsMega
// instances. With a single controller class there's nothing left to key on,
// so a plain module-scoped counter does the same job directly.
let openDropdownCount = 0;

class DropdownController {
  constructor(details) {
    this.details = details;

    this.classes = { bodyClass: 'has-dropdown-menu' };

    this.summaryElement = details.firstElementChild;
    this.contentElement = details.lastElementChild;

    this._open = details.hasAttribute('open');

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.detectHover = this.detectHover.bind(this);

    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    this.hoverIntent = createHoverIntent((shouldOpen) => {
      this.open = shouldOpen;
    });
    this.details.addEventListener('mouseenter', this.detectHover);
    this.details.addEventListener('mouseleave', this.detectHover);

    if (window.Shopify && Shopify.designMode) {
      this.details.addEventListener('shopify:block:select', () => {
        this.open = true;
      });
      this.details.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });

      // Sections are torn down and rebuilt constantly in the theme editor.
      // The old customized-built-in got disconnectedCallback for free from
      // the browser on removal; a plain class doesn't, so this listens for
      // the same section-teardown event BasicHeader already uses elsewhere
      // in this file and cleans up explicitly instead. Gated the same way
      // BasicHeader gates it -- theme-editor-only, no-op for real visitors.
      const section = this.details.closest('.shopify-section');
      if (section) {
        section.addEventListener('shopify:section:unload', () => this.destroy(), { once: true });
      }
    }

    dropdownControllers.set(details, this);
  }

  destroy() {
    // Sections are torn down and rebuilt constantly in the theme editor.
    // Without this the document-level listeners and any pending hover timer
    // outlive the element.
    this.hoverIntent.cancel();
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscKeyPress);
    document.removeEventListener('focusout', this.handleFocusOut);
    if (this._open) {
      this._open = false;
      this.releaseBodyLock();
    }
    dropdownControllers.delete(this.details);
  }

  set open(value) {
    if (value === this._open) return;
    this._open = value;

    if (!this.details.isConnected) {
      this.details.toggleAttribute('open', value);
      return;
    }

    if (value) {
      this.show();
    } else {
      this.hide();
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    if (FoxTheme.config.isTouch) {
      return 'click';
    }
    return this.details.getAttribute('trigger') || 'click';
  }

  get level() {
    return this.details.hasAttribute('level') ? this.details.getAttribute('level') : 'top';
  }

  show() {
    // Safety net for the paths that never fire mouseenter: touch, click,
    // keyboard focus, and the theme editor's block:select.
    warmNavImages();

    openDropdownCount += 1;
    document.body.classList.add(this.classes.bodyClass);

    // Setting the attribute IS the reveal. CSS keys the fade and slide off
    // details[open], so there is no promise to await and no way for the
    // panel to end up open-but-invisible.
    this.details.setAttribute('open', '');

    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleEscKeyPress);
    document.addEventListener('focusout', this.handleFocusOut);

    this.needsReverse();
  }

  hide() {
    this.releaseBodyLock();

    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscKeyPress);
    document.removeEventListener('focusout', this.handleFocusOut);

    // Removed immediately. The old code had to defer this until its animation
    // resolved, because the UA stylesheet display:none's a <details> element's
    // content the moment `open` goes away. .dropdown/.mega-menu are now
    // display:block unconditionally, so the panel stays painted through the
    // whole fade-out and [open] is only a selector hook.
    this.details.removeAttribute('open');
  }

  releaseBodyLock() {
    openDropdownCount = Math.max(0, openDropdownCount - 1);
    document.body.classList.toggle(this.classes.bodyClass, openDropdownCount > 0);
  }

  handleSummaryClick(event) {
    event.preventDefault();

    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      this.open = !this.open;
    }
  }

  handleOutsideClick(event) {
    const closestDetails = event.target.closest('details');
    const isClickOnDetailsDropdown = !!(closestDetails && dropdownControllers.has(closestDetails));
    if (isOutsideClick(this.details, event) && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (!isEscapeKey(event)) return;

    const targetMenu = event.target.closest('details[open]');
    if (targetMenu) {
      // Route through the controller when the nearest open <details> is one
      // of ours (equivalent to what the overridden `open` setter used to do
      // automatically); otherwise fall back to setting `.open` directly, same
      // as before, so an unrelated open <details> (e.g. an accordion) still
      // closes via its own semantics rather than being ignored.
      const targetController = dropdownControllers.get(targetMenu);
      if (targetController) {
        targetController.open = false;
      } else {
        targetMenu.open = false;
      }
    } else {
      // Previously Escape did nothing unless focus happened to be inside an
      // open menu -- so Escape after a hover-open (where focus is still on
      // the body) left the menu stuck open.
      this.open = false;
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.details.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    // Warm regardless of trigger mode: a pointer entering the nav is the
    // earliest reliable signal that a panel is about to be looked at, and it
    // buys the image the whole hover-intent delay plus the open transition.
    if (event.type === 'mouseenter') warmNavImages();
    if (this.trigger !== 'hover') return;
    this.hoverIntent.schedule(event.type === 'mouseenter');
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

function initDropdownControllers(root) {
  root.querySelectorAll('[is="details-dropdown"], [is="details-mega"]').forEach((details) => {
    if (dropdownControllers.has(details)) return;
    new DropdownController(details);
  });
}

FoxTheme.DOMready(() => initDropdownControllers(document));

// The theme editor swaps in fresh section HTML (new DOM nodes, not upgrades
// of existing ones) whenever a merchant edits header/menu settings. The old
// customized-built-in elements auto-upgraded themselves the instant such a
// node was inserted into the document; a plain class needs an explicit
// re-scan of the newly-inserted section to pick those nodes up. Nothing on
// the live storefront dispatches this event -- only the theme editor preview
// does -- so this is a no-op for real visitors.
document.addEventListener('shopify:section:load', (event) => {
  initDropdownControllers(event.target);
});

// 2026-09-16 (mobile menu accordion rebuild): MenuDrawerDetails used to drive
// a bespoke drill-down panel stack (parent-scoping `.active`, a 100ms open
// delay, a hand-rolled rAF close animation) for `is="menu-drawer-details"`.
// Nothing renders that custom element any more -- menu-drawer-details.liquid
// now flattens mid-tier groups into inline eyebrow-labeled links inside the
// top-level panel, and the top level itself is a plain `is="accordion-group"`
// (theme.js), which already handles open/close/sibling-close/aria-expanded.
// See strategy/mobile-menu-accordion-plan-2026-09-16.md.

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get menuItems() {
    if (!this._menuItems) {
      this._menuItems = this.querySelectorAll('.menu-drawer__menus > li');
    }
    return this._menuItems;
  }

  prepareToShow() {
    super.prepareToShow();
    setTimeout(() => {
      this.animateMenuItems();
    }, 300);
  }

  animateMenuItems() {
    FoxTheme.Motion.animate(
      this.menuItems,
      {
        transform: ['translateX(-20px)', 'translateX(0)'],
        opacity: [0, 1],
      },
      {
        duration: 0.6,
        easing: [0.075, 0.82, 0.165, 1],
        delay: FoxTheme.Motion.stagger(0.1),
      }
    ).finished.then(() => {
      this.menuItems.forEach((item) => item.removeAttribute('style'));
    });
  }

  handleAfterHide() {
    super.handleAfterHide();
    // Collapse any expanded top-level accordion when the drawer closes, so
    // reopening the drawer always starts from the closed state rather than
    // showing whatever section was left open last time.
    setTimeout(() => {
      this.querySelectorAll('details[is="accordion-group"][open]').forEach((details) => {
        details.open = false;
      });
    });
  }

  connectedCallback() {
    super.connectedCallback();
    // 2026-09-24, own correction -- "transition on mobile looks odd. one
    // closes one slides in. web content flashes up in middle." This drawer
    // slides from the left, SearchDrawer slides from the right; the search
    // trigger deliberately has no aria-controls of its own (see
    // menu-drawer.liquid) so ModalComponent never auto-opens SearchDrawer
    // at the same instant this drawer starts closing. Instead: close this
    // drawer, wait for its close transition to actually finish (hide()
    // returns a promise that resolves on handleAfterHide), THEN open
    // SearchDrawer -- a clean sequential close-then-open, so the two
    // full-screen panels never animate over each other and the raw page
    // is never exposed mid-transition.
    const searchTrigger = this.querySelector('.menu-drawer__search-trigger');
    if (searchTrigger) {
      searchTrigger.addEventListener('click', async () => {
        await this.hide();
        // search.js is deferred -- wait for <search-drawer> to be defined
        // rather than calling show() on a not-yet-upgraded element (2026-10-03).
        await customElements.whenDefined('search-drawer');
        const searchDrawer = document.getElementById('SearchDrawer');
        if (searchDrawer) searchDrawer.show(searchTrigger);
      });
    }
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }
}
customElements.define('menu-product-list', MenuProductList);

class HeaderAccount extends HTMLElement {
  async connectedCallback() {
    const account = this.querySelector('shopify-account');

    if (!account) return;

    await customElements.whenDefined('shopify-account');

    this.injectAnimation(account);
    // this.watchDialog(account);
  }

  injectAnimation(account) {
    const shadow = account.shadowRoot;

    if (!shadow || shadow.querySelector('#custom-account-styles')) return;

    const style = document.createElement('style');
    style.id = 'custom-account-styles';

    style.textContent = `
      @keyframes dialogScaleIn {
        0% {
          transform: scale(0.8);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes dialogScaleOut {
        0% {
          transform: scale(1);
          opacity: 1;
        }

        100% {
          transform: scale(0.8);
          opacity: 0;
        }
      }
      @keyframes dialogBackdropIn {
        0% {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
      }
      @keyframes dialogBackdropOut {
        0% {
          opacity: 1;
        }

        100% {
          opacity: 0;
        }
      }
      /* 2026-10-11 owner: a centred card on every screen size, not an edge-to-edge
         sheet on phones; height follows the content, capped so it never fills the screen. */
      dialog {
        position: fixed !important;
        inset: 0 !important;
        margin: auto !important;
        width: min(380px, calc(100vw - 32px)) !important;
        max-width: calc(100vw - 32px) !important;
        height: fit-content !important;
        max-height: min(85vh, 640px) !important;
        /* always the off-white page colour, whatever section it opens from
           (it was taking the dark green of the homepage rewards block) */
        background-color: #fdfcf9 !important;
        color: #1a1915 !important;
        overflow-y: auto !important;
        border-radius: 12px !important;
      }
      @media (min-width: 751px) {
        dialog[open] {
          animation: dialogScaleIn 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing {
          animation: dialogScaleOut 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog[open]::backdrop {
          cursor: zoom-out;
          animation: dialogBackdropIn 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing::backdrop {
          animation: dialogBackdropOut 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
      }
    `;

    shadow.prepend(style);
  }

  watchDialog(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    const dialog = shadow.querySelector('dialog');
    if (!dialog) return;

    const update = () => {
      document.documentElement.classList.toggle('account-dialog-open', dialog.hasAttribute('open'));
    };

    update();

    new MutationObserver(update).observe(dialog, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }
}

customElements.define('header-account', HeaderAccount);

// 2026-10-11 owner: the homepage rewards button and the mobile menu also use <shopify-account>;
// give every instance the same centred-card styling as the header one.
customElements.whenDefined('shopify-account').then(() => {
  const styler = document.createElement('header-account');
  document.querySelectorAll('shopify-account').forEach((account) => {
    if (account.closest('header-account')) return;
    const apply = () => HeaderAccount.prototype.injectAnimation.call(styler, account);
    if (account.shadowRoot) apply();
    else requestAnimationFrame(apply);
  });
});
